import React, { useEffect, useMemo, useState, useContext } from "react";
import { PrivilegeContext } from "../../../context/privilegeContext";
import "./VehicleDetailTable.css";
import { Loader } from "@vds/loaders";
import { getVehicleDetailInformation } from "../../services/C4S Decommission - Replacement/vehicleManagement";
import WarningIcon from "../../assets/icons/warning.png";

// ─────────────────────────────────────────────────────────────────────────────
// VehicleDetailTable — expansion panel for NewGridComponent rows.
//
// Receives `gridTemplateColumns` (the parent grid's CSS grid template string)
// and lays itself out using the SAME template, so sub-columns snap to exactly
// the same pixel boundaries as the parent header columns at any viewport width.
//
// Expansion is only active when checkboxes are NOT shown (expand icon is
// disabled in bulk-selection mode).  Column mapping with rowActions+expander:
//   col 1 : Vehicle/Asset   (minmax 220px)
//   col 2 : Name            (1fr)
//   col 3 : License Plate   (1fr)
//   col 4 : Inst. Type      (1fr)
//   col 5 : Vehicle Note    (minmax 150px)
//   col 6 : Shipping Status (1fr)  ← sub "Sub Status" (shipping) aligns here
//   col 7 : Inst. Status    (1fr)  ← sub "Sub Status" (install)  aligns here
//   col 8 : Appt. Address   (1fr)
//   col 9 : Actions         (100px)
// ─────────────────────────────────────────────────────────────────────────────

const MAIN_CATEGORIES = ["Core - Main"];

// Helper function to check main category
const isMainCategory = (category) => MAIN_CATEGORIES.includes(category);

// Helper function to sort vehicles
const sortVehicles = (vehicles) => {
  return vehicles.slice().sort((a, b) => {
    const aHasMain = Array.isArray(a.items) && a.items.some((item) => isMainCategory(item.category));
    const bHasMain = Array.isArray(b.items) && b.items.some((item) => isMainCategory(item.category));
    if (aHasMain && !bHasMain) return -1;
    if (!aHasMain && bHasMain) return 1;
    return 0;
  });
};

// Helper function to sort items
const sortItems = (items) => {
  // Priority: Core - Main first (if present), otherwise Companion - Main first, then everything else.
  const CORE = "Core - Main";
  const COMPANION = "Companion - Main";

  const hasCore = items?.some((it) => it?.category === CORE);

  const priorityOf = (category) => {
    if (hasCore) return category === CORE ? 0 : 1; // Core first if exists
    return category === COMPANION ? 0 : 1; // else Companion first if exists
  };

  return items.slice().sort((a, b) => {
    const aP = priorityOf(a?.category);
    const bP = priorityOf(b?.category);
    if (aP !== bP) return aP - bP;
    return 0;
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Parse the parent gridTemplateColumns string to determine the 1-based
// CSS grid-column positions for each sub-table column.
//
// Expansion is disabled when checkboxes are shown, so we only handle the
// no-selection case.  The grid template in that mode is:
//   <8 data columns> <100px actions>
// i.e. 9 CSS grid columns total, no leading prefix columns.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tokenise a CSS grid-template-columns string into individual column tokens.
 * Correctly handles `minmax(…)` and other function notations that contain
 * spaces (e.g. "minmax(100px, 220px) 1fr 1fr").
 */
function tokenizeGridTemplate(template) {
  const tokens = [];
  let current = "";
  let depth = 0; // tracks nested parentheses
  for (let i = 0; i < template.length; i++) {
    const ch = template[i];
    if (ch === "(") {
      depth++;
      current += ch;
    } else if (ch === ")") {
      depth--;
      current += ch;
    } else if (/\s/.test(ch) && depth === 0) {
      if (current.length) {
        tokens.push(current);
        current = "";
      }
    } else {
      current += ch;
    }
  }
  if (current.length) tokens.push(current);
  return tokens;
}

function parseColPositions(gridTemplateColumns) {
  const tokens = gridTemplateColumns ? tokenizeGridTemplate(gridTemplateColumns.trim()) : [];
  // No prefix columns — expansion is only active when checkboxes are NOT shown,
  // so there are no leading 40px checkbox / expander columns.
  const totalCols = tokens.length || 9;
  const dataStart = 1; // 1-based: first column is always the first data column
  return {
    subCol: dataStart, // Subscription (spans 3: Vehicle/Asset + Name + License Plate)
    catCol: dataStart + 3, // Category     aligns with Inst.Type        (data col 4)
    hwCol: dataStart + 4, // Hardware     aligns with Vehicle Note      (data col 5)
    shipStatusCol: dataStart + 5, // Sub Status   aligns with Shipping Status  (data col 6)
    instStatusCol: dataStart + 6, // Sub Status   aligns with Inst.Status      (data col 7)
    apptCol: dataStart + 7, // Appointment Address                       (data col 8)
    totalCols,
  };
}

// ── Shared cell-style objects ──────────────────────────────────────────────
const HEADER_CELL_BASE = {
  padding: "8px 12px",
  fontSize: "12px",
  fontWeight: 700,
  lineHeight: "16px",
  fontFamily: "Verizon-NHG-eDS",
  color: "#333332",
  borderBottom: "1px solid #d8dada",
  background: "#f9f9f9",
  display: "flex",
  alignItems: "center",
};

const DATA_CELL_BASE = {
  padding: "8px 12px",
  fontSize: "12px",
  fontFamily: "Verizon-NHG-eDS",
  color: "#000",
  fontWeight: 400,
  display: "flex",
  alignItems: "flex-start",
  flexDirection: "column",
};

// A positioned grid cell — all values are 1-based CSS grid coordinates
// gridRow / gridCol are explicit placement (e.g. "2" or "2 / span 3")
const GCell = ({ gridRow, gridCol, colSpan = 1, style = {}, children }) => (
  <div
    style={{
      gridColumn: colSpan > 1 ? `${gridCol} / span ${colSpan}` : String(gridCol),
      gridRow: gridRow,
      ...style,
    }}
  >
    {children}
  </div>
);

const FALLBACK_TEMPLATE = "minmax(100px,220px) 1fr 1fr 1fr minmax(100px,150px) 1fr 1fr 1fr 100px";

const VehicleDetailTable = ({ row, gridTemplateColumns }) => {
  const [data, setData] = useState(null);
  const [isLoading, setLoader] = useState(false);
  const { cspInfo } = useContext(PrivilegeContext);
  const cspData = cspInfo.data.data;

  useEffect(() => {
    setLoader(true);
    const crmRefId = cspData?.CSP?.[0]?.CRM_Ref_ID;
    if (row?.bundleId && row?.id && crmRefId) {
      getVehicleDetailInformation(row.bundleId, crmRefId, row.id)
        .then((result) => {
          setData(result?.data?.data);
          setLoader(false);
        })
        .catch(() => {
          setData(null);
          setLoader(false);
        });
    }
  }, [row, cspData]);

  const sortedVehicles = useMemo(() => {
    if (!data?.vehicles) return [];
    return sortVehicles(data.vehicles);
  }, [data?.vehicles]);
  const colPos = useMemo(() => parseColPositions(gridTemplateColumns), [gridTemplateColumns]);

  const template = gridTemplateColumns || FALLBACK_TEMPLATE;

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 80 }}>
        <Loader active fullscreen={false} />
      </div>
    );
  }

  if (!data || !Array.isArray(data.vehicles) || data.vehicles.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 80, color: "#6f7171", fontSize: 12 }}>
        No data available
      </div>
    );
  }
  const { subCol, catCol, hwCol, shipStatusCol, instStatusCol, apptCol, totalCols } = colPos;

  // ── Build all rows for ONE flat CSS grid ──────────────────────────────────
  // We use a single <div display:grid> so that gridRow/gridColumn placements
  // actually align.  Row 1 = header, rows 2..N = data.
  let currentRow = 2; // row 1 is the header
  const gridCells = [];

  // Header cells (row 1)
  const hStyle = (extra = {}) => ({ ...HEADER_CELL_BASE, ...extra });
  gridCells.push(
    <GCell key="h-sub" gridRow={1} gridCol={subCol} colSpan={3} style={hStyle({ backgroundColor: "white" })}>
      Subscription
    </GCell>,
    <GCell key="h-cat" gridRow={1} gridCol={catCol} style={hStyle({ backgroundColor: "white", borderLeft: "1px solid #DDDAD4" })}>
      Category
    </GCell>,
    <GCell key="h-hw" gridRow={1} gridCol={hwCol} style={hStyle({ backgroundColor: "white", borderLeft: "1px solid #DDDAD4" })}>
      Hardware
    </GCell>,
    <GCell key="h-ship" gridRow={1} gridCol={shipStatusCol} style={hStyle({ backgroundColor: "white", borderLeft: "1px solid #DDDAD4" })}>
      Sub Status
    </GCell>,
    <GCell key="h-inst" gridRow={1} gridCol={instStatusCol} style={hStyle({ backgroundColor: "white", borderLeft: "1px solid #DDDAD4" })}>
      Sub Status
    </GCell>,
    <GCell
      key="h-appt"
      gridRow={1}
      gridCol={apptCol}
      colSpan={2}
      style={hStyle({ backgroundColor: "white", borderLeft: "1px solid #DDDAD4", borderRight: "none" })}
    >
      Appointment Address
    </GCell>
  );

  sortedVehicles.forEach((sub, subIdx) => {
    if (!sub.items || !Array.isArray(sub.items) || sub.items.length === 0) return;
    const sortedItems = sortItems(sub.items);
    const rowCount = sortedItems.length;
    const subStartRow = currentRow;
    const subEndRow = currentRow + rowCount - 1;

    // Subscription cell — spans all item rows
    const subBorder = "1px solid #ddd";
    gridCells.push(
      <GCell
        key={`sub-${subIdx}`}
        gridRow={`${subStartRow} / ${subEndRow + 1}`}
        gridCol={subCol}
        colSpan={3}
        style={{ ...DATA_CELL_BASE, color: "#666", borderBottom: subBorder, borderRight: "1px solid #DDDAD4" }}
      >
        <div style={{ fontWeight: 600, color: "#333" }}>
          {sub.name?.trim() || "--"} ({sub.mmid?.trim() || "--"})
        </div>
        <div style={{ marginTop: 4 }}>
          <span className="vehicle-detail-metadata-label">TECH REF ID:</span>
          <span className="vehicle-detail-metadata-value">{sub.techRef?.trim() || "--"}</span>
        </div>
      </GCell>
    );

    // Install-status cell — spans all item rows
    gridCells.push(
      <GCell
        key={`inst-${subIdx}`}
        gridRow={`${subStartRow} / ${subEndRow + 1}`}
        gridCol={instStatusCol}
        style={{ ...DATA_CELL_BASE, borderBottom: subBorder, borderRight: "1px solid #DDDAD4" }}
      >
        <span className={sub.appointmentStatus ? "vehicle-detail-grey-badge" : ""}>{sub.appointmentStatus?.trim() || "--"}</span>
        {sub.qaVisitNeeded && (
          <div style={{ color: "#FF8027", display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
            <img src={WarningIcon} alt="warning-icon" width={12} height={12} />
            <span style={{ paddingLeft: 2 }}>Needs QA</span>
          </div>
        )}
      </GCell>
    );

    // Appointment Address cell — spans all item rows
    gridCells.push(
      <GCell
        key={`appt-${subIdx}`}
        gridRow={`${subStartRow} / ${subEndRow + 1}`}
        colSpan={2}
        gridCol={apptCol}
        style={{ ...DATA_CELL_BASE, borderBottom: subBorder, borderRight: "none" }}
      >
        {sub.appointmentAddress?.trim() || "--"}
      </GCell>
    );

    // Per-item cells (Category, Hardware, Sub Status shipping)
    sortedItems.forEach((item, itemIdx) => {
      const r = subStartRow + itemIdx;
      const isLastInSub = itemIdx === rowCount - 1;
      const borderBottom = isLastInSub ? "1px solid #ddd" : "1px solid #eee";

      gridCells.push(
        <GCell
          key={`cat-${subIdx}-${itemIdx}`}
          gridRow={r}
          gridCol={catCol}
          style={{ ...DATA_CELL_BASE, borderBottom: isLastInSub ? "1px solid #ddd" : "none", borderRight: "1px solid #DDDAD4" }}
        >
          {item.category?.trim() || "--"}
        </GCell>,

        <GCell
          key={`hw-${subIdx}-${itemIdx}`}
          gridRow={r}
          gridCol={hwCol}
          style={{ ...DATA_CELL_BASE, borderBottom: isLastInSub ? "1px solid #ddd" : "none", borderRight: "1px solid #DDDAD4" }}
        >
          <div>{item.hardware?.trim() || "--"}</div>
          {item.hardwareId && <div style={{ color: "#666", fontWeight: 400, fontSize: 11, marginTop: 2 }}>{item.hardwareId}</div>}
        </GCell>,

        <GCell
          key={`ship-${subIdx}-${itemIdx}`}
          gridRow={r}
          gridCol={shipStatusCol}
          style={{ ...DATA_CELL_BASE, borderBottom: isLastInSub ? "1px solid #ddd" : "none", borderRight: "1px solid #DDDAD4" }}
        >
          <span className={item.status ? "vehicle-detail-badge" : ""}>{item.status?.trim() || "--"}</span>
          <div style={{ fontSize: 11, marginTop: 4, color: "#666" }}>
            {item.dateLabel ?? "--"}: {item.date?.trim() || "--"}
          </div>
        </GCell>
      );
    });

    currentRow += rowCount;
  });

  // Total rows in the grid = 1 header + all data rows
  const totalRows = currentRow - 1;

  return (
    <div className="vehicle-detail-table-container">
      {/* Single flat CSS grid — header row + all data rows together */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: template,
          gridTemplateRows: `repeat(${totalRows}, auto)`,
          width: "100%",
          borderTop: "2px solid #d8dada",
        }}
      >
        {gridCells}
      </div>

      {/* Footer */}
      <div className="vehicle-detail-table-footer">
        <button className="vehicle-history-btn">View History</button>
      </div>
    </div>
  );
};

export default VehicleDetailTable;
